/**
 * Featured brands rail — seamless loop and arrow stepping.
 *
 * The feed is rendered twice server-side. Scrolling past the halfway point
 * jumps back by half the width, and scrolling to the start jumps forward by
 * half: the reader lands on the identical tile in the other copy, so the rail
 * runs forever in both directions without a visible seam.
 *
 * Native swipe, trackpad and click-drag all keep working — the arrows are an
 * addition, not a replacement, so nothing here calls preventDefault on a
 * scroll.
 */
const STEP = 720;

class BrandRail {
  constructor(root) {
    this.rail = root.querySelector('[data-brail-rail]');
    if (!this.rail) return;

    root.querySelector('[data-brail-prev]')?.addEventListener('click', () => this.#step(-1));
    root.querySelector('[data-brail-next]')?.addEventListener('click', () => this.#step(1));

    this.last = this.rail.scrollLeft;
    // Passive: this only ever reads scrollLeft and writes it back.
    this.rail.addEventListener('scroll', () => this.#wrap(), { passive: true });
  }

  /** Half the doubled feed — the distance between a tile and its twin. */
  #half() {
    return this.rail.scrollWidth / 2;
  }

  /**
   * The jump has to clear the edges by a few pixels. Landing exactly on 0 or
   * on the midpoint would re-trigger this on the next scroll event and the
   * rail would sit there flipping between the two copies.
   *
   * Two guards, both learned the hard way:
   *
   *   · Not while an arrow step is animating. A smooth scroll away from 0
   *     reports its first frames still inside the "<= 4" band, so the
   *     backward wrap fired, jumped forward half the rail, and the forward
   *     wrap pulled it straight back — the arrows appeared to do nothing.
   *   · The backward wrap only when actually moving backward. Otherwise the
   *     tail of any scroll that settles near zero triggers it.
   */
  #wrap() {
    if (this.stepping) return;
    const half = this.#half();
    if (half < 1) return;

    const x = this.rail.scrollLeft;
    if (x >= half + 40) this.rail.scrollLeft -= half;
    else if (x <= 4 && x < this.last) this.rail.scrollLeft += half;
    this.last = this.rail.scrollLeft;
  }

  /** Wrap first, then step — otherwise a smooth scroll animates across the seam. */
  #step(direction) {
    const half = this.#half();
    if (direction > 0 && this.rail.scrollLeft >= half) this.rail.scrollLeft -= half;
    if (direction < 0 && this.rail.scrollLeft < 40) this.rail.scrollLeft += half;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.stepping = true;

    const from = this.rail.scrollLeft;
    const delta = direction * STEP;
    this.rail.scrollBy({ left: delta, behavior: reduced ? 'auto' : 'smooth' });

    // Some environments accept a smooth scroll and never animate it — the call
    // returns, nothing moves, and the arrow looks broken. Shortly after, if the
    // rail has not budged, do it outright. A real smooth scroll has always
    // started by then, so this never fights one.
    //
    // A timer rather than requestAnimationFrame on purpose: rAF does not fire
    // at all while the page is hidden or backgrounded, which is exactly when
    // smooth scrolling is also skipped — the fallback would be missing in the
    // one case it exists for.
    clearTimeout(this.fallbackTimer);
    this.fallbackTimer = setTimeout(() => {
      if (this.rail.scrollLeft === from) this.rail.scrollLeft = from + delta;
    }, 60);
    clearTimeout(this.stepTimer);
    // Long enough for the smooth scroll to settle before wrapping resumes.
    this.stepTimer = setTimeout(() => {
      this.stepping = false;
      this.last = this.rail.scrollLeft;
      this.#wrap();
    }, reduced ? 50 : 700);
  }
}

const attach = (root = document) => {
  for (const el of root.querySelectorAll('.bb-brail')) {
    if (el.dataset.brailBound) continue;
    el.dataset.brailBound = '1';
    new BrandRail(el);
  }
};

attach();
document.addEventListener('shopify:section:load', (e) => attach(e.target));
